import { isSecondPhotographerAddon, isVideoAddon } from '@/lib/addons/addonFamilies';
import { getStaffingNeeds, roleOccupant, ROLE_LABELS } from '@/lib/booking/staffing';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { Badge } from '@/components/admin/ui/badge';
import { Button } from '@/components/admin/ui/button';
import { Input } from '@/components/admin/ui/input';
import { Textarea } from '@/components/admin/ui/textarea';
import { format } from 'date-fns';
import { parseDateOnly } from '@lib/utils';
import { ArrowLeft, Mail, Phone, Calendar, DollarSign, Star, UserX, UserPlus, UserMinus, AlertCircle, Flag, MessageSquarePlus, MessageSquare, Bell, CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, AlertCircle as AlertCircleIcon, MapPin, ExternalLink, Pencil, Check, X, Copy, FileText } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/admin/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/admin/ui/collapsible';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/admin/ui/alert-dialog';
import { Label } from '@/components/admin/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/admin/ui/select';
import { useChatStore } from '@/hooks/useChatStore';
import { supabaseAdmin } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import AdminChatPanel from '@/components/admin/AdminChatPanel';
import BookingChatHistory from '@/components/admin/BookingChatHistory';
import { useAdminAuth } from '@/hooks/useAdminAuth';
import { recallListPath } from '@/lib/admin/listMemory';
import { fetchInBatches } from '@utils/batchSupabaseQueries';
import { consoleLink } from '@/lib/staging/consoleLink';
import { useStagingOptional } from '@/staging/StagingLayout';
import TalentRecordsCard from '@/staging/components/TalentRecordsCard';

// L+P Company Profile Photographer IDs
const LP_PHOTO_PHOTOGRAPHER_ID = '410935f9-6034-40b3-a65f-e9863de60e58';
const LP_VIDEO_PHOTOGRAPHER_ID = '7c7191dc-c5e5-43ed-b772-083463a00aa4';
const LP_PLACEHOLDER_IDS = [LP_PHOTO_PHOTOGRAPHER_ID, LP_VIDEO_PHOTOGRAPHER_ID];

const isVideoPackageType = (booking: any) => {
  const packageValues = [
    booking?.package_type,
    booking?.personalization_data?.package_type,
    booking?.personalization_data?.packageType,
    booking?.personalization_data?.package?.type,
    booking?.personalization_data?.package?.packageType,
  ];

  return packageValues.some(value => {
    const normalized = String(value || '').replace(/[_-\s]/g, '').toLowerCase();
    return normalized.includes('video');
  });
};

const TalentDetail = () => {
  const { talentId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentUser } = useChatStore();
  const { toast } = useToast();
  const { user } = useAdminAuth();
  // Inside the new console, sections follow the View As role (a simulation of what that role sees).
  const staging = useStagingOptional();
  const viewRole = staging?.role ?? user?.role;
  const [talent, setTalent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [talentBookings, setTalentBookings] = useState<any[]>([]);
  const [availableBookings, setAvailableBookings] = useState<any[]>([]);
  const [bookingNotes, setBookingNotes] = useState<Record<string, any[]>>({});
  const [logisticsData, setLogisticsData] = useState<Record<string, any>>({});
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState('');
  const [assignmentRole, setAssignmentRole] = useState<'photographer' | 'videographer' | 'second_photographer' | 'second_videographer'>('photographer');
  const [isFlagDialogOpen, setIsFlagDialogOpen] = useState(false);
  const [flagType, setFlagType] = useState('');
  const [flagSeverity, setFlagSeverity] = useState('');
  const [flagReason, setFlagReason] = useState('');
  const [flagAssignee, setFlagAssignee] = useState('');
  const [unassignConfirmOpen, setUnassignConfirmOpen] = useState(false);
  const [assignmentToUnassign, setAssignmentToUnassign] = useState<string | null>(null);
  const [roleToUnassign, setRoleToUnassign] = useState<'photographer' | 'videographer' | 'second_photographer' | null>(null);
  const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
  const [assignErrorOpen, setAssignErrorOpen] = useState(false);
  const [assignErrorMessage, setAssignErrorMessage] = useState('');
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [selectedAssignmentForNote, setSelectedAssignmentForNote] = useState<string | null>(null);
  const [newNote, setNewNote] = useState('');
  const [isAdminChatOpen, setIsAdminChatOpen] = useState(false);
  const [talentNotes, setTalentNotes] = useState<any[]>([]);
  const [newTalentNote, setNewTalentNote] = useState('');
  const [flagDepartment, setFlagDepartment] = useState('');
  const [departmentUsers, setDepartmentUsers] = useState<any[]>([]);
  const [blacklistConfirmOpen, setBlacklistConfirmOpen] = useState(false);
  const [blacklistReason, setBlacklistReason] = useState('');
  const [expandedBooking, setExpandedBooking] = useState<string | null>(null);
  const [isEditingRate, setIsEditingRate] = useState(false);
  const [editedRate, setEditedRate] = useState<number>(40);

  useEffect(() => {
    const fetchTalentData = async () => {
      if (!talentId) return;

      try {
        console.time('🚀 TalentDetails Load Time');

        // STEP 1: Fetch talent data first (needed for user_id)
        console.time('📊 Fetch Talent Data');
        const { data: talentData, error: talentError } = await supabaseAdmin
        .from('photographers')
        .select(`
          *,
          users!photographers_user_id_fkey (
            id,
            full_name,
            email,
            phone,
            is_blacklisted,
            delete_reason
          )
        `)
        .eq('id', talentId)
        .maybeSingle();
        console.timeEnd('📊 Fetch Talent Data');

        if (talentError) throw talentError;

        if (talentData) {
          setTalent({
            id: talentData.id,
            user_id: talentData.user_id,
            name: talentData.users?.full_name || '',
            email: talentData.users?.email || '',
            phone: talentData.users?.phone || '',
            role: 'Photographer',
            currentRate: Number(talentData.hourly_rate) || 40,
            status: talentData.status ||'Good Standing',
            isLoveAndPhotosChoice: talentData.lnp_choice,
            onboardingDate: new Date(talentData.created_at),
            city: talentData.city,
            state: talentData.state,
            zipCode: talentData.zip_code,
            blacklistReason: talentData.users?.delete_reason || null
          });
        }

        const db = supabaseAdmin as any;

        // STEP 2: Run all independent queries in PARALLEL
        console.time('⚡ Parallel Queries');
        const [
          bookingsResult,
          availableBookingsResult,
          talentNotesResult
        ] = await Promise.all([
          // Bookings for this talent with all related data
          supabaseAdmin
            .from('bookings')
            .select(`
              id,
              event_date,
              event_type,
              venue_name,
              location_city,
              location_state,
              total_amount,
              amount_paid,
              remaining_balance,
              booking_status,
              payment_status,
              package_type,
              hours_booked,
              talent_paid,
              talent_payment_amount,
              photographer_id,
              videographer_id,
              second_photographer_id,
              second_videographer_id,
              client_outreach_confirmed,
              client_outreach_confirmed_at,
              event_ready_confirmed,
              event_ready_confirmed_at,
              customer:users!bookings_customer_id_fkey (
                id,
                full_name,
                email
              ),
              photographer_reminder_attempts!photographer_reminder_attempts_booking_id_fkey(
                id,
                reminder_count,
                email_subject,
                email_sent_to,
                recipient_role,
                delivery_status,
                error_message,
                resend_email_id,
                customer_name,
                event_location,
                event_date,
                created_at
              ),
              email_schedules!email_schedules_booking_id_fkey(
                id,
                email_type,
                status,
                recipient_email,
                recipient_name,
                recipient_type,
                scheduled_for,
                sent_at,
                created_at,
                error_message,
                resend_email_id
              ),
              talent_quizzes!talent_quizzes_booking_id_fkey(
                id,
                status,
                role,
                passed,
                score,
                total_questions,
                due_at,
                assigned_at,
                started_at,
                completed_at,
                email_sent_at,
                admin_notification_sent,
                admin_notified_at,
                customer_name,
                event_location,
                venue_name
              )
            `)
            .or(`photographer_id.eq.${talentId},videographer_id.eq.${talentId},second_photographer_id.eq.${talentId},second_videographer_id.eq.${talentId}`)
            .neq('booking_status', 'draft')
            .order('event_date', { ascending: true }),

          // Available bookings: any booking with an open talent slot.
          // Open means: photographer or videographer is an L+P placeholder, the second-photographer slot is
          // empty OR holds a placeholder, or a video package has no videographer at all (the slot is null,
          // not a placeholder, on bookings created through checkout).
          supabaseAdmin
            .from('bookings')
            .select(`
              id,
              event_date,
              location_city,
              location_state,
              package_type,
              total_amount,
              remaining_balance,
              customer_id,
              photographer_id,
              videographer_id,
              second_photographer_id,
              second_videographer_id,
              personalization_data,
              is_test,
              photographer:photographers!bookings_photographer_id_fkey(id, user:users!photographers_user_id_fkey(full_name)),
              videographer:photographers!bookings_videographer_id_fkey(id, user:users!photographers_user_id_fkey(full_name)),
              second_photographer:photographers!bookings_second_photographer_id_fkey(id, user:users!photographers_user_id_fkey(full_name)),
              second_videographer:photographers!bookings_second_videographer_id_fkey(id, user:users!photographers_user_id_fkey(full_name)),
              add_ons:booking_addons!booking_addons_booking_id_fkey(
                addon:addons!booking_addons_addon_id_fkey(code)
              )
            `)
            .or(`photographer_id.in.(${LP_PHOTO_PHOTOGRAPHER_ID},${LP_VIDEO_PHOTOGRAPHER_ID}),videographer_id.in.(${LP_PHOTO_PHOTOGRAPHER_ID},${LP_VIDEO_PHOTOGRAPHER_ID}),second_photographer_id.is.null,second_photographer_id.in.(${LP_PHOTO_PHOTOGRAPHER_ID},${LP_VIDEO_PHOTOGRAPHER_ID}),and(videographer_id.is.null,package_type.in.(photoVideo,photoAndVideo,videoOnly))`)
            .in('booking_status', ['pending', 'declined_by_talent', 'confirmed', 'in_progress'])
            .gte('event_date', new Date().toISOString().split('T')[0])
            .order('event_date', { ascending: true }),

          // Talent notes
          db.from('talent_notes')
            .select('*')
            .eq('talent_id', talentId)
            .order('created_at', { ascending: false })
        ]);
        console.timeEnd('⚡ Parallel Queries');

        // Handle bookings result
        const { data: bookingsData, error: bookingsError } = bookingsResult;
        if (bookingsError) throw bookingsError;

        // STEP 3: Fetch booking notes (depends on booking IDs)
        if (bookingsData && bookingsData.length > 0) {
          console.time('📝 Fetch Booking Notes');
          const bookingIds = bookingsData.map((b: any) => b.id);

          const { data: notesData } = await db
            .from('booking_notes')
            .select('*')
            .in('booking_id', bookingIds)
            .order('created_at', { ascending: false });
          console.timeEnd('📝 Fetch Booking Notes');

          // Group notes by booking_id
          const notesByBooking: Record<string, any[]> = {};
          if (notesData) {
            notesData.forEach((note: any) => {
              if (!notesByBooking[note.booking_id]) {
                notesByBooking[note.booking_id] = [];
              }
              notesByBooking[note.booking_id].push({
                id: note.id,
                author: note.created_by_name,
                date: new Date(note.created_at),
                text: note.note
              });
            });
          }

          setBookingNotes(notesByBooking);

          // Fetch logistics questionnaires for these bookings
          console.time('📋 Fetch Logistics Questionnaires');
          const { data: logisticsDataArray } = await supabaseAdmin
            .from('logistics_questionnaire')
            .select('*')
            .in('booking_id', bookingIds);
          console.timeEnd('📋 Fetch Logistics Questionnaires');

          if (logisticsDataArray) {
            const logisticsMap: Record<string, any> = {};
            logisticsDataArray.forEach((logistics: any) => {
              logisticsMap[logistics.booking_id] = logistics;
            });
            setLogisticsData(logisticsMap);
          }

          setTalentBookings(bookingsData.map((booking: any) => {
            // Calculate talent rate: $40/hour for photographers
            const hoursBooked = Number(booking.hours_booked || 0);
            const talentRate = hoursBooked * (Number((talentData as any)?.hourly_rate) || 40); // the talent's own hourly rate ($40 when none is set)
            // Actual amount paid (in dollars, converted from cents)
            const actualPaidAmount = booking.talent_payment_amount ? booking.talent_payment_amount / 100 : null;

            // Determine which role(s) this talent has in the booking
            const isPhotographer = booking.photographer_id === talentId;
            const isVideographer = booking.videographer_id === talentId;
            const isSecondPhotographer = booking.second_photographer_id === talentId;
            const isSecondVideographer = booking.second_videographer_id === talentId;

            return {
              id: booking.id,
              bookingId: booking.id,
              talentId: talentId,
              customerId: booking.customer?.id || null,
              clientName: booking.customer?.full_name || 'Unknown',
              clientEmail: booking.customer?.email || '',
              eventCity: booking.location_city || '',
              eventState: booking.location_state || '',
              eventDate: parseDateOnly(booking.event_date),
              rate: talentRate, // Talent rate = hours × $40
              hoursBooked: hoursBooked,
              actualPaidAmount: actualPaidAmount, // Actual amount paid to talent
              totalAmount: Number(booking.total_amount || 0),
              amountPaid: Number(booking.amount_paid || 0),
              remainingBalance: Number(booking.remaining_balance || 0),
              status: booking.booking_status || 'Pending',
              packageType: booking.package_type || '',
              talent_paid: booking.talent_paid || false,
              notes: notesByBooking[booking.id] || [],
              contactStatus: 'completed' as const,
              contactDeadline: new Date(),
              contactLogs: [],
              client_outreach_confirmed: booking.client_outreach_confirmed || false,
              client_outreach_confirmed_at: booking.client_outreach_confirmed_at,
              event_ready_confirmed: booking.event_ready_confirmed || false,
              event_ready_confirmed_at: booking.event_ready_confirmed_at,
              photographer_reminder_attempts: booking.photographer_reminder_attempts || [],
              email_schedules: booking.email_schedules || [],
              talent_quizzes: booking.talent_quizzes || [],
              // Track which role(s) this talent has
              isPhotographer,
              isVideographer,
              isSecondPhotographer,
              isSecondVideographer
            };
          }));
        }

        // Handle available bookings result
        const { data: availableBookingsData, error: availableError } = availableBookingsResult;
        if (availableError) throw availableError;

        if (availableBookingsData && availableBookingsData.length > 0) {
          // Fetch customer data for available bookings
          const customerIds = availableBookingsData.map((b: any) => b.customer_id).filter(Boolean);
          const { data: customersData } = await supabaseAdmin
            .from('users')
            .select('id, full_name')
            .in('id', customerIds);

          const customersMap = new Map(customersData?.map((c: any) => [c.id, c.full_name]) || []);

          // Helper to check if booking has second-shooter addon
          const hasSecondShooterAddon = (personalizationData: any) => {
            const addons = personalizationData?.addons || [];
            return addons.some((addon: any) =>
              isSecondPhotographerAddon(addon) ||
              addon.title?.toLowerCase().includes('second')
            );
          };

          setAvailableBookings(
            availableBookingsData.map((b: any) => {
              // One rule for "still needs someone", shared with the Bookings list (Traci, 4 Sep 2026)
              const needs = getStaffingNeeds(b);
              const needsPhotographer = needs.photographer;
              const needsVideographer = needs.videographer;
              const needs2ndPhotographer = needs.secondPhotographer;
              const needs2ndVideographer = needs.secondVideographer;
              // Real people already holding a role (placeholders don't count) — the dialog warns before overwriting
              const takenBy = (role: 'photographer' | 'videographer' | 'second_photographer' | 'second_videographer', slot: any) =>
                roleOccupant(b, role).kind === 'talent' ? (slot?.user?.full_name || 'another talent') : null;

              return {
                id: b.id,
                isTest: b.is_test === true,
                clientName: customersMap.get(b.customer_id) || 'Unknown Client',
                eventDate: parseDateOnly(b.event_date),
                eventCity: b.location_city || '',
                eventState: b.location_state || '',
                packageType: b.package_type || '',
                remainingBalance: Number(b.remaining_balance || 0),
                totalAmount: Number(b.total_amount || 0),
                needsPhotographer,
                needsVideographer,
                needs2ndPhotographer,
                needs2ndVideographer,
                taken: {
                  photographer: takenBy('photographer', b.photographer),
                  videographer: takenBy('videographer', b.videographer),
                  second_photographer: takenBy('second_photographer', b.second_photographer),
                  second_videographer: takenBy('second_videographer', b.second_videographer),
                },
              };
            })
            // Only bookings that still need someone: the same set the Bookings list badges
            .filter((b: any) => b.needsPhotographer || b.needsVideographer || b.needs2ndPhotographer || b.needs2ndVideographer)
          );
        } else {
          setAvailableBookings([]);
        }

        // Handle talent notes
        const { data: talentNotesData, error: talentNotesError } = talentNotesResult;
        if (talentNotesError) {
          console.error('Error fetching talent notes:', talentNotesError);
        } else {
          setTalentNotes(talentNotesData || []);
        }

        // Note: photographer_reminder_attempts, email_schedules, and talent_quizzes
        // are now fetched with each booking via foreign key relationships

        console.timeEnd('🚀 TalentDetails Load Time');
      } catch (error) {
        console.error('Error fetching talent data:', error);
        toast({
          title: "Error",
          description: "Failed to load talent data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchTalentData();
  }, [talentId, toast]);

  useEffect(() => {
    const fetchDepartmentUsers = async () => {
      if (!flagDepartment) {
        setDepartmentUsers([]);
        return;
      }
      const db = supabaseAdmin as any;

      // First, get all user_ids for this role
      const { data: roleData, error: roleError } = await db
        .from('user_roles')
        .select('user_id')
        .eq('role', flagDepartment as any);

      if (roleError || !roleData || roleData.length === 0) {
        setDepartmentUsers([]);
        return;
      }

      // Then fetch profiles for those user_ids
      const userIds = roleData.map((r: any) => r.user_id);
      const { data: profileData, error: profileError } = await fetchInBatches(db, 'profiles', 'id, full_name, email', 'id', userIds);

      if (profileData) {
        setDepartmentUsers(profileData.map((profile: any) => ({
          id: profile.id,
          name: profile.full_name,
          email: profile.email,
        })));
      }
    };

    fetchDepartmentUsers();
  }, [flagDepartment]);

  // Auto-open chat if openChat parameter is present
  useEffect(() => {
    const openChat = searchParams.get('openChat');
    if (openChat === 'true' && !loading && talent) {
      setIsAdminChatOpen(true);
      // Remove the parameter from URL to clean it up
      searchParams.delete('openChat');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, loading, talent, setSearchParams]);

  if (loading) {
    return (
      <div className="p-8">
        <div className="text-center">
          <p>Loading talent data...</p>
        </div>
      </div>
    );
  }

  if (!talent) {
    return (
      <div className="p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Talent not found</h1>
          <Button onClick={() => navigate(consoleLink(recallListPath('talent', '/lp-console/talent')))}>
            Back to Talent
          </Button>
        </div>
      </div>
    );
  }

  const activeAssignments = talentBookings.filter(b => 
    new Date(b.eventDate) >= new Date() && b.status !== 'Completed'
  );
  
  const completedAssignments = talentBookings.filter(b =>
    new Date(b.eventDate) < new Date() || b.status === 'Completed'
  );

  const toggleLoveAndPhotosChoice = async () => {
  if (!talent?.id) return;

  // Determine new value
  const newValue = !talent.isLoveAndPhotosChoice;

  try {
    // Optimistically update local state
    setTalent(prev => prev ? { ...prev, isLoveAndPhotosChoice: newValue } : null);

    // Update database
    const { error } = await supabaseAdmin
      .from('photographers')
      .update({ lnp_choice: newValue })
      .eq('id', talent.id);

    if (error) {
      console.error('Error updating Love & Photos Choice:', error.message);
      toast.error('Failed to update Love & Photos Choice');
      // Revert if failed
      setTalent(prev => prev ? { ...prev, isLoveAndPhotosChoice: !newValue } : null);
      return;
    }

    toast.success(
      newValue
        ? 'Talent added to Love & Photos Choice.'
        : 'Talent removed from Love & Photos Choice.'
    );
  } catch (err) {
    console.error('Unexpected error toggling Love & Photos Choice:', err);
    toast.error('Unexpected error updating Love & Photos Choice');
  }
};



  const toggleBlacklist = async () => {
    if (!talent?.id || !talent?.user_id) return;

    // Determine the new status and blacklist value
    const newStatus = talent.status === 'Blacklisted' ? 'Good Standing' : 'Blacklisted';
    const isBlacklisted = newStatus === 'Blacklisted';

    // Validate reason is required when blacklisting
    if (isBlacklisted && !blacklistReason.trim()) {
      toast({
        title: 'Reason Required',
        description: 'Please provide a reason for blacklisting this talent.',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Optimistically update UI
      const newBlacklistReason = isBlacklisted ? blacklistReason.trim() : null;
      setTalent(prev => prev ? { ...prev, status: newStatus, blacklistReason: newBlacklistReason } : null);

      // Update photographers.status for admin visibility
      const { error: photographerError } = await supabaseAdmin
        .from('photographers')
        .update({ status: newStatus })
        .eq('id', talent.id);

      if (photographerError) {
        console.error('Error updating photographer status:', photographerError.message);
        toast({
          title: 'Error',
          description: 'Failed to update photographer status',
          variant: 'destructive'
        });
        // Revert UI if DB update fails
        setTalent(prev => prev ? { ...prev, status: talent.status, blacklistReason: talent.blacklistReason } : null);
        return;
      }

      // Update users.is_blacklisted and delete_reason
      const { error: userError } = await supabaseAdmin
        .from('users')
        .update({
          is_blacklisted: isBlacklisted,
          delete_reason: newBlacklistReason
        })
        .eq('id', talent.user_id);

      if (userError) {
        console.error('Error updating user blacklist status:', userError.message);
        toast({
          title: 'Error',
          description: 'Failed to update user blacklist status',
          variant: 'destructive'
        });
        // Revert photographer status if user update fails
        await supabaseAdmin
          .from('photographers')
          .update({ status: talent.status })
          .eq('id', talent.id);
        setTalent(prev => prev ? { ...prev, status: talent.status, blacklistReason: talent.blacklistReason } : null);
        return;
      }

      // Log to audit log
      if (user?.id) {
        const { error: auditError } = await supabaseAdmin
          .from('admin_audit_log')
          .insert({
            user_id: talent.user_id,
            actor_id: user.id,
            action: isBlacklisted ? 'talent_blacklisted' : 'talent_blacklist_removed',
            payload: {
              talent_id: talent.id,
              talent_name: talent.name,
              reason: newBlacklistReason,
              previous_status: talent.status,
              new_status: newStatus
            }
          });

        if (auditError) {
          console.error('Failed to log to audit log:', auditError);
          // Don't fail the operation if audit logging fails
        } else {
          console.log('✅ Audit log entry created successfully');
        }
      } else {
        console.warn('⚠️ User ID not available for audit logging. User object:', user);
      }

      toast({
        title: 'Success',
        description: newStatus === 'Blacklisted'
          ? 'Talent successfully blacklisted and hidden from search.'
          : 'Talent removed from blacklist and visible in search.'
      });

      // Clear the reason input and close dialog
      setBlacklistReason('');
      setBlacklistConfirmOpen(false);
    } catch (err) {
      console.error('Unexpected error toggling blacklist:', err);
      toast({
        title: 'Error',
        description: 'Unexpected error updating blacklist status',
        variant: 'destructive'
      });
    }
  };

  // The booking chosen in the Assign dialog, and who (if anyone) already holds the chosen role on it
  const selectedBookingRow = availableBookings.find(b => b.id === selectedBooking);
  const roleTakenBy: string | null = selectedBookingRow?.taken?.[assignmentRole] || null;

  const handleAssignToBooking = () => {
    if (selectedBooking && talent) {
      setAssignConfirmOpen(true);
    }
  };

  const confirmAssignToBooking = async () => {
    if (selectedBooking && talent) {
      try {
        // Use the API endpoint to assign talent (handles notifications, availability checks, QBO reminders)
        // Send the staff session so the server can record WHO made the assignment
        const { data: sessionData } = await supabaseAdmin.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        const response = await fetch(`/api/booking/${selectedBooking}/request-photographer`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
          },
          body: JSON.stringify({
            photographerId: talent.id,
            role: assignmentRole,
            adminOverride: true
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          const message = errorData.message || `Assignment failed (${response.status})`;
          const details = errorData.details ? `: ${errorData.details}` : '';
          throw new Error(`${message}${details}`);
        }

        // Fetch the updated booking details
        const db = supabaseAdmin as any;
        const { data: bookingData, error: fetchError } = await db
          .from('bookings')
          .select('*')
          .eq('id', selectedBooking)
          .single();

        if (fetchError) throw fetchError;

        if (bookingData) {
          // Calculate talent rate: $40/hour for photographers
          const hoursBooked = Number(bookingData.hours_booked || 0);
          const talentRate = hoursBooked * (Number(talent?.currentRate) || 40); // the talent's own hourly rate ($40 when none is set)
          // Actual amount paid (in dollars, converted from cents)
          const actualPaidAmount = bookingData.talent_payment_amount ? bookingData.talent_payment_amount / 100 : null;

          const newAssignment = {
            id: bookingData.id,
            bookingId: bookingData.id,
            talentId: talent.id,
            clientName: bookingData.client_name || 'Unknown',
            eventCity: bookingData.event_city,
            eventState: bookingData.event_state,
            eventDate: parseDateOnly(bookingData.event_date),
            rate: talentRate, // Talent rate = hours × $40
            hoursBooked: hoursBooked,
            actualPaidAmount: actualPaidAmount, // Actual amount paid to talent
            status: bookingData.status,
            packageType: bookingData.package_type,
            totalAmount: Number(bookingData.total_amount),
            amountPaid: Number(bookingData.amount_paid),
            remainingBalance: Number(bookingData.remaining_balance),
            talent_paid: bookingData.talent_paid || false,
            notes: [],
            contactStatus: 'pending' as const,
            contactDeadline: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
            contactLogs: [],
            client_outreach_confirmed: bookingData.client_outreach_confirmed || false,
            client_outreach_confirmed_at: bookingData.client_outreach_confirmed_at,
            event_ready_confirmed: bookingData.event_ready_confirmed || false,
            event_ready_confirmed_at: bookingData.event_ready_confirmed_at
          };

          setTalentBookings(prev => [...prev, newAssignment]);

          const roleLabel = assignmentRole === 'photographer' ? 'Photographer'
            : assignmentRole === 'videographer' ? 'Videographer'
            : '2nd Photographer';

          toast({
            title: "Talent Assigned",
            description: `Successfully assigned ${talent.name} as ${roleLabel} to booking.`
          });
        }

        setSelectedBooking('');
        setAssignmentRole('photographer'); // Reset to default
        setIsAssignDialogOpen(false);
        setAssignConfirmOpen(false);
      } catch (error) {
        console.error('Error assigning talent:', error);
        // Show error in a dialog popup so users clearly see why assignment failed
        setAssignErrorMessage(error instanceof Error ? error.message : "Failed to assign talent to booking.");
        setAssignErrorOpen(true);
        // Close the confirmation dialog but keep the assign dialog open
        setAssignConfirmOpen(false);
      }
    }
  };

  const handleUnassign = (assignmentId: string, role: 'photographer' | 'videographer' | 'second_photographer') => {
    setAssignmentToUnassign(assignmentId);
    setRoleToUnassign(role);
    setUnassignConfirmOpen(true);
  };

  const confirmUnassign = async () => {
    if (assignmentToUnassign && roleToUnassign) {
      try {
        // Default L+P photographer IDs for unassigned bookings
        const DEFAULT_PHOTO_ID = '410935f9-6034-40b3-a65f-e9863de60e58';
        const DEFAULT_VIDEO_ID = '7c7191dc-c5e5-43ed-b772-083463a00aa4';

        // Determine which field to update based on role
        let updateData: Record<string, string | null> = {};
        let roleLabel = '';

        switch (roleToUnassign) {
          case 'photographer':
            updateData = { photographer_id: DEFAULT_PHOTO_ID };
            roleLabel = 'Photographer';
            break;
          case 'videographer':
            updateData = { videographer_id: DEFAULT_VIDEO_ID };
            roleLabel = 'Videographer';
            break;
          case 'second_photographer':
            updateData = { second_photographer_id: null };
            roleLabel = '2nd Photographer';
            break;
        }

        const { error } = await supabaseAdmin
          .from('bookings')
          .update(updateData)
          .eq('id', assignmentToUnassign);

        if (error) throw error;

        // Log talent unassignment to audit log for accountability tracking
        const { error: auditError } = await supabaseAdmin
          .from('admin_audit_log')
          .insert({
            user_id: null, // Will be set to booking customer_id if needed
            actor_id: user?.id || null,
            action: 'talent_unassignment',
            payload: {
              booking_id: assignmentToUnassign,
              role: roleToUnassign,
              previous_talent_id: talent?.id,
              previous_talent_name: talent?.name || 'Unknown',
              unassigned_to: roleToUnassign === 'second_photographer' ? null : (roleToUnassign === 'photographer' ? DEFAULT_PHOTO_ID : DEFAULT_VIDEO_ID),
              changed_at: new Date().toISOString()
            }
          });

        if (auditError) {
          console.warn('⚠️ Failed to log talent unassignment to audit log:', auditError);
        } else {
          console.log('📝 Talent unassignment logged to audit:', {
            booking: assignmentToUnassign,
            role: roleToUnassign,
            talent: talent?.name
          });
        }

        // Update local state based on role
        if (roleToUnassign === 'second_photographer') {
          // For 2nd photographer, just remove from local list since slot becomes empty
          setTalentBookings(prev => prev.filter(a => a.id !== assignmentToUnassign));
        } else {
          // For photographer/videographer, update the role flag
          setTalentBookings(prev => prev.map(a => {
            if (a.id === assignmentToUnassign) {
              const updated = { ...a };
              if (roleToUnassign === 'photographer') updated.isPhotographer = false;
              if (roleToUnassign === 'videographer') updated.isVideographer = false;
              // If talent has no more roles in this booking, remove it
              if (!updated.isPhotographer && !updated.isVideographer && !updated.isSecondPhotographer) {
                return null;
              }
              return updated;
            }
            return a;
          }).filter(Boolean) as any[]);
        }

        toast({
          title: `${roleLabel} Unassigned`,
          description: roleToUnassign === 'second_photographer'
            ? "2nd Photographer slot is now empty."
            : `${roleLabel} reassigned to default L+P placeholder.`
        });

        setUnassignConfirmOpen(false);
        setAssignmentToUnassign(null);
        setRoleToUnassign(null);
      } catch (error) {
        console.error('Error unassigning talent:', error);
        toast({
          title: "Error",
          description: "Failed to unassign talent from booking.",
          variant: "destructive"
        });
      }
    }
  };

  const handleFlagAccount = async () => {
    if (flagType && flagSeverity && flagReason && talent && user && flagDepartment) {
      if (!talent.user_id) {
        toast({
          title: 'Cannot Flag Talent',
          description: 'This talent is not linked to a user account.',
          variant: 'destructive',
        });
        return;
      }
      try {
        const insertData: any = {
          user_id: talent.user_id,
          severity: flagSeverity.toLowerCase() as 'low' | 'medium' | 'high' | 'critical',
          reason: `${flagType}: ${flagReason}`,
          flagged_by: user.id,
          assigned_to_role: flagDepartment as any,
          status: 'open',
          notes: flagAssignee ? `Assigned to: ${flagAssignee}` : undefined,
        };

        if (flagAssignee) {
          insertData.assigned_to = flagAssignee;
        }

        const { error } = await supabaseAdmin
          .from('flagged_accounts')
          .insert([insertData]);

        if (error) throw error;

        toast({
          title: 'Account Flagged',
          description: `${talent.name}'s account has been flagged successfully.`,
        });
        
        // Reset form
        setFlagType('');
        setFlagSeverity('');
        setFlagReason('');
        setFlagDepartment('');
        setFlagAssignee('');
        setIsFlagDialogOpen(false);
      } catch (error) {
        console.error('Error flagging account:', error);
        toast({
          title: 'Error',
          description: 'Failed to flag account. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const handleAddNote = async () => {
    if (newNote.trim() && selectedAssignmentForNote && user) {
      try {
        const db = supabaseAdmin as any;
        const { data: profileData } = await db
          .from('profiles')
          .select('name')
          .eq('id', user.id)
          .single();

        const { data, error } = await supabaseAdmin
          .from('booking_notes')
          .insert({
            booking_id: selectedAssignmentForNote,
            note: newNote.trim(),
            created_by: user.id,
            created_by_name: profileData?.name || user.email || 'Unknown'
          })
          .select()
          .single();

        if (error) throw error;

        if (data) {
          const newNoteObj = {
            id: data.id,
            author: data.created_by_name,
            date: new Date(data.created_at),
            text: data.note
          };

          // Update local state
          setBookingNotes(prev => ({
            ...prev,
            [selectedAssignmentForNote]: [newNoteObj, ...(prev[selectedAssignmentForNote] || [])]
          }));

          setTalentBookings(prev => prev.map(a => 
            a.id === selectedAssignmentForNote 
              ? { ...a, notes: [newNoteObj, ...a.notes] }
              : a
          ));

          toast({
            title: "Note Added",
            description: "Your note has been saved successfully."
          });
        }

        setNewNote('');
        setNoteDialogOpen(false);
        setSelectedAssignmentForNote(null);
      } catch (error) {
        console.error('Error adding note:', error);
        toast({
          title: "Error",
          description: "Failed to add note. Please try again.",
          variant: "destructive"
        });
      }
    }
  };

  const handleAddTalentNote = async () => {
    if (!newTalentNote.trim() || !user || !talentId) return;

    try {
      const { data, error } = await supabaseAdmin
        .from('talent_notes')
        .insert({
          talent_id: talentId,
          note: newTalentNote.trim(),
          created_by: user.id,
          created_by_name: user.name || user.email,
        })
        .select()
        .single();

      if (error) throw error;

      setTalentNotes([data, ...talentNotes]);
      setNewTalentNote('');
      
      toast({
        title: 'Note Added',
        description: 'Talent note has been added successfully.',
      });
    } catch (error) {
      console.error('Error adding talent note:', error);
      toast({
        title: 'Error',
        description: 'Failed to add note. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveRate = async () => {
    if (!talent?.id || editedRate < 0) return;

    try {
      const { error } = await supabaseAdmin
        .from('photographers')
        .update({ hourly_rate: editedRate })
        .eq('id', talent.id);

      if (error) throw error;

      setTalent((prev: any) => prev ? { ...prev, currentRate: editedRate } : null);
      setIsEditingRate(false);

      toast({
        title: 'Rate Updated',
        description: `Hourly rate updated to $${editedRate}.`,
      });
    } catch (error) {
      console.error('Error updating hourly rate:', error);
      toast({
        title: 'Error',
        description: 'Failed to update hourly rate.',
        variant: 'destructive',
      });
    }
  };

  const openNoteDialog = (assignmentId: string) => {
    setSelectedAssignmentForNote(assignmentId);
    setNoteDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Talent Confirmed':
        return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20';
      case 'Talent Reached Out to Client':
        return 'bg-purple-500/10 text-purple-700 dark:text-purple-400 border-purple-500/20';
      case 'Media Uploaded':
        return 'bg-cyan-500/10 text-cyan-700 dark:text-cyan-400 border-cyan-500/20';
      case 'Talent Paid':
        return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20';
      case 'Job Completed':
        return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/20';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  return (
    <div className="p-8">
      <Button 
        variant="ghost" 
        onClick={() => navigate(consoleLink(recallListPath('talent', '/lp-console/talent')))}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Talent
      </Button>

      <div className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl flex items-center gap-2">
                  {talent.name}
                  {talent.isLoveAndPhotosChoice && (
                    <Star className="h-6 w-6 fill-amber-500 text-amber-500" />
                  )}
                </CardTitle>
                <CardDescription className="mt-1">{talent.role}</CardDescription>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <div className="flex items-center gap-1">
                    <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                      Photographer ID: {talent.id}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => {
                        navigator.clipboard.writeText(talent.id);
                        toast({
                          title: 'Copied',
                          description: 'Photographer ID copied to clipboard',
                        });
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  {talent.user_id && (
                    <div className="flex items-center gap-1">
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                        User ID: {talent.user_id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          navigator.clipboard.writeText(talent.user_id);
                          toast({
                            title: 'Copied',
                            description: 'User ID copied to clipboard',
                          });
                        }}
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2">
                <Badge 
                  variant="outline" 
                  className={talent.status === 'Good Standing' 
                    ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
                    : 'bg-destructive/10 text-destructive border-destructive/20'
                  }
                >
                  {talent.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium">{talent.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium">{talent.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">
                      {talent.city && talent.state
                        ? `${talent.city}, ${talent.state}`
                        : talent.city || talent.state || '—'
                      }
                    </p>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => setIsAdminChatOpen(true)}
                    variant="outline"
                    className="w-full"
                  >
                    <MessageSquarePlus className="h-4 w-4 mr-2" />
                    Chat with Talent
                  </Button>
                  <Button
                    onClick={() => window.open(`/photographer/${talent.id}`, '_blank')}
                    variant="outline"
                    className="w-full"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Public Profile
                  </Button>
                </div>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Onboarding Date</p>
                    <p className="font-medium">{format(talent.onboardingDate, 'MMM d, yyyy')}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Current Rate</p>
                    {isEditingRate ? (
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg">$</span>
                        <Input
                          type="number"
                          value={editedRate}
                          onChange={(e) => setEditedRate(Number(e.target.value))}
                          className="w-24 h-8"
                          min={0}
                          autoFocus
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={handleSaveRate}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setIsEditingRate(false);
                            setEditedRate(talent.currentRate);
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-lg">${talent.currentRate.toLocaleString()}</p>
                        {(viewRole === 'admin' || viewRole === 'talent_coordinator') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground"
                            onClick={() => {
                              setEditedRate(talent.currentRate);
                              setIsEditingRate(true);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Star className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Jobs Completed</p>
                    <p className="font-semibold text-lg">{completedAssignments.length}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-4 border-t">
              {/* Only show Love & Photos Choice button to admin and talent_coordinator */}
              {(viewRole === 'admin' || viewRole === 'talent_coordinator') && (
                <Button
                  className="border border-gray-300 text-gray-800 hover:bg-gray-100"
                  variant={talent.isLoveAndPhotosChoice ? "default" : "outline"}
                  size="sm"
                  onClick={toggleLoveAndPhotosChoice}
                >
                  <Star className="h-4 w-4 mr-2" />
                  {talent.isLoveAndPhotosChoice ? 'Remove' : 'Add'} Love & Photos Choice
                </Button>
              )}

              {/* Only show blacklist button to admin and talent_coordinator */}
              {(viewRole === 'admin' || viewRole === 'talent_coordinator') && (
                <>
                  <Button
                    variant={talent.status === 'Blacklisted' ? 'default' : 'destructive'}
                    size="sm"
                    onClick={() => setBlacklistConfirmOpen(true)}
                    className={talent.status !== 'Blacklisted' ? 'text-white hover:text-white' : ''}
                  >
                    {talent.status === 'Blacklisted' ? (
                      <><UserPlus className="h-4 w-4 mr-2" />Remove from Blacklist</>
                    ) : (
                      <><UserX className="h-4 w-4 mr-2" />Blacklist Talent</>
                    )}
                  </Button>

                  {/* Blacklist Confirmation Dialog */}
                  <AlertDialog open={blacklistConfirmOpen} onOpenChange={(open) => {
                    setBlacklistConfirmOpen(open);
                    if (!open) setBlacklistReason(''); // Clear reason when closing
                  }}>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {talent.status === 'Blacklisted' ? 'Remove from Blacklist' : 'Blacklist Talent'}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {talent.status === 'Blacklisted'
                            ? `Are you sure you want to remove ${talent.name} from the blacklist? This will make their profile visible in search results again.`
                            : `Are you sure you want to blacklist ${talent.name}? This will immediately hide their profile from all search results and prevent customers from booking them.`
                          }
                        </AlertDialogDescription>
                      </AlertDialogHeader>

                      {/* Reason textarea - only show when blacklisting */}
                      {talent.status !== 'Blacklisted' && (
                        <div className="py-4">
                          <Label htmlFor="blacklist-reason" className="text-sm font-medium mb-2 block">
                            Reason for Blacklisting <span className="text-red-500">*</span>
                          </Label>
                          <Textarea
                            id="blacklist-reason"
                            placeholder="Provide a detailed reason for blacklisting this talent..."
                            value={blacklistReason}
                            onChange={(e) => setBlacklistReason(e.target.value)}
                            className="min-h-[100px]"
                            required
                          />
                          <p className="text-xs text-muted-foreground mt-2">
                            This reason will be logged in the audit trail and displayed on the talent&apos;s profile.
                          </p>
                        </div>
                      )}

                      <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setBlacklistReason('')}>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={toggleBlacklist}
                          className={talent.status !== 'Blacklisted' ? 'bg-red-600 hover:bg-red-700' : ''}
                        >
                          {talent.status === 'Blacklisted' ? 'Remove from Blacklist' : 'Blacklist Talent'}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </>
              )}

              {/* Only show assign to booking to admin and talent_coordinator */}
              {(viewRole === 'admin' || viewRole === 'talent_coordinator') && (
                <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <UserPlus className="h-4 w-4 mr-2" />
                      Assign to Booking
                    </Button>
                  </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Assign to Booking</DialogTitle>
                    <DialogDescription>
                      Manually assign this talent to an available booking
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="booking">Select Booking</Label>
                      <Select value={selectedBooking} onValueChange={setSelectedBooking}>
                        <SelectTrigger id="booking" className="mt-2">
                          <SelectValue placeholder="Choose a booking" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableBookings.map(booking => {
                            // Build needs indicator
                            const needs = [];
                            if (booking.needsPhotographer) needs.push('📷');
                            if (booking.needsVideographer) needs.push('🎥');
                            if (booking.needs2ndPhotographer) needs.push('📷²'); if (booking.needs2ndVideographer) needs.push('🎥²');
                            const needsStr = needs.length > 0 ? ` [Needs: ${needs.join(' ')}]` : '';

                            return (
                              <SelectItem key={booking.id} value={booking.id}>
                                {booking.isTest ? 'TEST · ' : ''}{booking.clientName} — {format(booking.eventDate, 'MMM d, yyyy')} • {booking.eventCity || booking.eventState || 'No location'}{needsStr}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="role">Assignment Role</Label>
                      <Select value={assignmentRole} onValueChange={(value: 'photographer' | 'videographer' | 'second_photographer' | 'second_videographer') => setAssignmentRole(value)}>
                        <SelectTrigger id="role" className="mt-2">
                          <SelectValue placeholder="Choose a role" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="photographer">Photographer{selectedBookingRow?.taken?.photographer ? ` — assigned to ${selectedBookingRow.taken.photographer}` : ''}</SelectItem>
                          <SelectItem value="videographer">Videographer{selectedBookingRow?.taken?.videographer ? ` — assigned to ${selectedBookingRow.taken.videographer}` : ''}</SelectItem>
                          <SelectItem value="second_photographer">2nd Photographer{selectedBookingRow?.taken?.second_photographer ? ` — assigned to ${selectedBookingRow.taken.second_photographer}` : ''}</SelectItem>
                          <SelectItem value="second_videographer">2nd Videographer{selectedBookingRow?.taken?.second_videographer ? ` — assigned to ${selectedBookingRow.taken.second_videographer}` : ''}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {roleTakenBy && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-300">
                        <strong>{ROLE_LABELS[assignmentRole]} is already assigned to {roleTakenBy}.</strong>{' '}
                        Choose the role that still needs someone. To replace {roleTakenBy}, unassign them from the booking first.
                      </div>
                    )}
                    <Button
                      onClick={handleAssignToBooking}
                      disabled={!selectedBooking || Boolean(roleTakenBy)}
                    >
                      Assign as {assignmentRole === 'photographer' ? 'Photographer' : assignmentRole === 'videographer' ? 'Videographer' : '2nd Photographer'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              )}

              {/* Assign Confirmation Dialog */}
              {(viewRole === 'admin' || viewRole === 'talent_coordinator') && (
              <AlertDialog open={assignConfirmOpen} onOpenChange={setAssignConfirmOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Confirm Assignment</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to assign {talent.name} as{' '}
                      <strong>{assignmentRole === 'photographer' ? 'Photographer' : assignmentRole === 'videographer' ? 'Videographer' : '2nd Photographer'}</strong>{' '}
                      to this booking?
                      {selectedBooking && ` (${availableBookings.find(b => b.id === selectedBooking)?.clientName})`}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={confirmAssignToBooking}>Confirm Assignment</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}

              {/* Assignment Error Dialog - shows clear message when assignment fails */}
              <AlertDialog open={assignErrorOpen} onOpenChange={setAssignErrorOpen}>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="h-5 w-5" />
                      Assignment Failed
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-base pt-2">
                      {assignErrorMessage}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogAction onClick={() => setAssignErrorOpen(false)}>
                      OK
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <Dialog open={isFlagDialogOpen} onOpenChange={setIsFlagDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <Flag className="h-4 w-4 mr-2" />
                    Flag Account
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Flag Account</DialogTitle>
                    <DialogDescription>
                      Create a flag for this talent account for review
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div>
                      <Label htmlFor="flag-type">Flag Type</Label>
                      <Select value={flagType} onValueChange={setFlagType}>
                        <SelectTrigger id="flag-type" className="mt-2">
                          <SelectValue placeholder="Select flag type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Payment Issue">Payment Issue</SelectItem>
                          <SelectItem value="Communication Issue">Communication Issue</SelectItem>
                          <SelectItem value="Quality Issue">Quality Issue</SelectItem>
                          <SelectItem value="Behavior Issue">Behavior Issue</SelectItem>
                          <SelectItem value="No Show">No Show</SelectItem>
                          <SelectItem value="Contract Violation">Contract Violation</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="flag-severity">Severity</Label>
                      <Select value={flagSeverity} onValueChange={setFlagSeverity}>
                        <SelectTrigger id="flag-severity" className="mt-2">
                          <SelectValue placeholder="Select severity" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Low">Low</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Critical">Critical</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="flag-reason">Reason</Label>
                      <Textarea
                        id="flag-reason"
                        placeholder="Describe the issue..."
                        value={flagReason}
                        onChange={(e) => setFlagReason(e.target.value)}
                        className="mt-2 min-h-[100px]"
                      />
                    </div>
                    <div>
                      <Label htmlFor="flag-department">Assign to Department *</Label>
                      <Select value={flagDepartment} onValueChange={(value) => {
                        setFlagDepartment(value);
                        setFlagAssignee('');
                      }}>
                        <SelectTrigger id="flag-department" className="mt-2">
                          <SelectValue placeholder="Select department" />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          <SelectItem value="sales">Sales</SelectItem>
                          <SelectItem value="billing">Billing</SelectItem>
                          <SelectItem value="talent_coordinator">Talent Coordinator</SelectItem>
                          <SelectItem value="editing">Editing</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="flag-assignee">Assign to Reviewer (Optional)</Label>
                      <Select value={flagAssignee} onValueChange={setFlagAssignee} disabled={!flagDepartment}>
                        <SelectTrigger id="flag-assignee" className="mt-2">
                          <SelectValue placeholder={flagDepartment ? "Select team member" : "Select department first"} />
                        </SelectTrigger>
                        <SelectContent className="bg-background z-50">
                          {departmentUsers.map((user) => (
                            <SelectItem key={user.id} value={user.id}>
                              {user.name} ({user.email})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleFlagAccount} 
                      disabled={!flagType || !flagSeverity || !flagReason || !flagDepartment}
                      className="w-full"
                    >
                      Create Flag
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardContent>
        </Card>

        {/* Blacklist Reason Card - Only shown if talent is blacklisted */}
        {talent.status === 'Blacklisted' && talent.blacklistReason && (
          <Card className="border-red-200 bg-red-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                Blacklist Information
              </CardTitle>
              <CardDescription>This talent has been blacklisted from the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div>
                  <Label className="text-sm font-medium text-red-700">Reason for Blacklisting:</Label>
                  <div className="mt-2 p-4 bg-white border border-red-200 rounded-lg">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{talent.blacklistReason}</p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground pt-2 border-t border-red-200">
                  <p>This talent&apos;s profile is hidden from search results and they cannot accept new bookings.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Active Bookings */}
        <CardContent>
          {activeAssignments.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
              <p className="text-muted-foreground">No active bookings</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeAssignments.map((assignment) => (
                <Collapsible
                  key={assignment.id}
                  open={expandedBooking === assignment.id}
                  onOpenChange={(open) => setExpandedBooking(open ? assignment.id : null)}
                >
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-lg">
                          {assignment.customerId ? (
                            <span
                              className="text-primary hover:underline cursor-pointer"
                              onClick={() => navigate(consoleLink(`/lp-console/clients/${assignment.customerId}`))}
                            >
                              {assignment.clientName}
                            </span>
                          ) : (
                            assignment.clientName
                          )}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {assignment.eventCity && assignment.eventState
                            ? `${assignment.eventCity}, ${assignment.eventState}`
                            : assignment.eventCity || assignment.eventState || ''}
                          {' • '}
                          {format(new Date(assignment.eventDate), 'MMMM d, yyyy')}
                        </p>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Role badges */}
                        {assignment.isPhotographer && (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            📷 Photographer
                          </Badge>
                        )}
                        {assignment.isVideographer && (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            🎥 Videographer
                          </Badge>
                        )}
                        {assignment.isSecondPhotographer && (
                          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                            📷² 2nd Photographer
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={
                            assignment.status === 'Completed'
                              ? 'bg-green-100 text-green-700 border-green-200'
                              : assignment.status === 'Pending'
                              ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                              : 'bg-muted text-muted-foreground'
                          }
                        >
                          {assignment.status}
                        </Badge>
                      </div>
                    </div>

                    {/* Photographer Readiness Indicators - Always Visible */}
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">Photographer Confirmations:</p>
                      <div className="flex flex-wrap gap-2">
                        {/* Client Outreach Status */}
                        {assignment.client_outreach_confirmed ? (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Client Outreach Complete
                            {assignment.client_outreach_confirmed_at && (
                              <span className="ml-1 text-xs">
                                ({format(new Date(assignment.client_outreach_confirmed_at), 'MMM d')})
                              </span>
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                            <Clock className="h-3 w-3 mr-1" />
                            Client Outreach Pending
                          </Badge>
                        )}

                        {/* Event Ready Status */}
                        {assignment.event_ready_confirmed ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Event Ready
                            {assignment.event_ready_confirmed_at && (
                              <span className="ml-1 text-xs">
                                ({format(new Date(assignment.event_ready_confirmed_at), 'MMM d')})
                              </span>
                            )}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                            <Clock className="h-3 w-3 mr-1" />
                            Event Ready Pending
                          </Badge>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-end pt-2 border-t">
                      <CollapsibleTrigger asChild>
                        <Button variant="outline" size="sm">
                          {expandedBooking === assignment.id ? 'Hide Details' : 'Show Details'}
                        </Button>
                      </CollapsibleTrigger>
                    </div>

                    {/* Collapsible Details */}
                    <CollapsibleContent className="space-y-4 pt-4 border-t">
                      {/* Email Notifications for This Booking */}
                      <div>
                        <h5 className="font-semibold mb-3 flex items-center gap-2">
                          <Mail className="h-4 w-4" />
                          Email Notifications ({assignment.email_schedules?.length || 0})
                        </h5>
                        {!assignment.email_schedules || assignment.email_schedules.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No emails sent for this booking yet</p>
                        ) : (
                          <div className="space-y-2">
                            {assignment.email_schedules
                              .sort((a: any, b: any) => new Date(b.scheduled_for).getTime() - new Date(a.scheduled_for).getTime())
                              .map((email: any) => (
                              <Collapsible key={email.id}>
                                <div className="border rounded-lg p-3 bg-muted/30">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                      <Badge
                                        variant={
                                          email.status === 'sent' ? 'default' :
                                          email.status === 'failed' ? 'destructive' :
                                          email.status === 'cancelled' ? 'secondary' :
                                          'outline'
                                        }
                                        className={`shrink-0 ${email.status === 'failed' ? 'text-white' : ''}`}
                                      >
                                        {email.status}
                                      </Badge>
                                      <Badge variant="outline" className="text-xs shrink-0">
                                        {email.email_type}
                                      </Badge>
                                      <span className="text-sm truncate">{email.recipient_email}</span>
                                      {email.error_message && (
                                        <AlertCircleIcon className="h-4 w-4 text-destructive shrink-0" />
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                                        {format(new Date(email.scheduled_for), 'MMM d, h:mm a')}
                                      </span>
                                      <CollapsibleTrigger asChild>
                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                          <ChevronDown className="h-4 w-4" />
                                        </Button>
                                      </CollapsibleTrigger>
                                    </div>
                                  </div>

                                  <CollapsibleContent className="pt-3 border-t mt-3">
                                    <div className="space-y-2 text-xs text-muted-foreground">
                                      {email.recipient_name && (
                                        <p><span className="font-medium">Name:</span> {email.recipient_name}</p>
                                      )}
                                      <p><span className="font-medium">Recipient Type:</span> {email.recipient_type}</p>
                                      <p><span className="font-medium">Scheduled For:</span> {format(new Date(email.scheduled_for), 'MMM d, yyyy h:mm a')}</p>
                                      {email.sent_at && (
                                        <p><span className="font-medium">Sent At:</span> {format(new Date(email.sent_at), 'MMM d, yyyy h:mm a')}</p>
                                      )}
                                      {email.resend_email_id && (
                                        <p className="font-mono"><span className="font-medium font-sans">Email ID:</span> {email.resend_email_id}</p>
                                      )}
                                      <p><span className="font-medium">Created:</span> {format(new Date(email.created_at), 'MMM d, yyyy h:mm a')}</p>
                                    </div>
                                    {email.error_message && (
                                      <div className="mt-3 p-2 bg-destructive/10 rounded text-xs text-destructive">
                                        <span className="font-medium">Error:</span> {email.error_message}
                                      </div>
                                    )}
                                  </CollapsibleContent>
                                </div>
                              </Collapsible>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Photo Upload Reminders for This Booking */}
                      <div className="pt-3 border-t mt-3">
                        <h5 className="font-semibold mb-3 flex items-center gap-2">
                          <Bell className="h-4 w-4" />
                          Photo Upload Reminders ({assignment.photographer_reminder_attempts?.length || 0})
                        </h5>
                        {!assignment.photographer_reminder_attempts || assignment.photographer_reminder_attempts.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No photo upload reminders sent for this booking yet</p>
                        ) : (
                          <div className="space-y-2">
                            {assignment.photographer_reminder_attempts
                              .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                              .map((attempt: any) => (
                              <div key={attempt.id} className="border rounded-lg p-3 bg-muted/30">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge
                                        variant={
                                          attempt.delivery_status === 'delivered' ? 'default' :
                                          attempt.delivery_status === 'failed' ? 'destructive' :
                                          'secondary'
                                        }
                                        className={`shrink-0 ${attempt.delivery_status === 'failed' ? 'text-white' : ''}`}
                                      >
                                        {attempt.delivery_status || 'pending'}
                                      </Badge>
                                      <span className="text-xs text-muted-foreground">
                                        Reminder #{attempt.reminder_count}
                                      </span>
                                    </div>
                                    <p className="text-sm font-medium mb-1">
                                      {attempt.email_subject}
                                    </p>
                                    <div className="text-xs text-muted-foreground space-y-1">
                                      <p>To: {attempt.email_sent_to} ({attempt.recipient_role})</p>
                                      {attempt.resend_email_id && (
                                        <p className="font-mono">Email ID: {attempt.resend_email_id}</p>
                                      )}
                                    </div>
                                    {attempt.error_message && (
                                      <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                                        Error: {attempt.error_message}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground whitespace-nowrap ml-4">
                                    {format(new Date(attempt.created_at), 'MMM d, h:mm a')}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pre-Event Quizzes for This Booking */}
                      <div className="pt-3 border-t mt-3">
                        <h5 className="font-semibold mb-3 flex items-center gap-2">
                          <CheckCircle className="h-4 w-4" />
                          Pre-Event Quizzes ({assignment.talent_quizzes?.length || 0})
                        </h5>
                        {!assignment.talent_quizzes || assignment.talent_quizzes.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No quizzes assigned for this booking yet</p>
                        ) : (
                          <div className="space-y-2">
                            {assignment.talent_quizzes.map((quiz: any) => (
                              <div key={quiz.id} className="border rounded-lg p-3 bg-muted/30">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Badge
                                        className="text-white"
                                        variant={
                                          quiz.status === 'completed' && quiz.passed ? 'default' :
                                          quiz.status === 'completed' && !quiz.passed ? 'destructive' :
                                          quiz.status === 'in_progress' ? 'secondary' :
                                          new Date(quiz.due_at) < new Date() ? 'destructive' :
                                          'outline'
                                        }
                                      >
                                        {quiz.status === 'completed' && quiz.passed && (
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                        )}
                                        {quiz.status === 'completed' && !quiz.passed && (
                                          <XCircle className="h-3 w-3 mr-1" />
                                        )}
                                        {quiz.status === 'in_progress' && (
                                          <Clock className="h-3 w-3 mr-1" />
                                        )}
                                        {quiz.status === 'pending' && new Date(quiz.due_at) < new Date() && (
                                          <AlertTriangle className="h-3 w-3 mr-1" />
                                        )}
                                        {quiz.status === 'completed'
                                          ? (quiz.passed ? 'Passed' : 'Failed')
                                          : quiz.status === 'in_progress'
                                          ? 'In Progress'
                                          : new Date(quiz.due_at) < new Date()
                                          ? 'Overdue'
                                          : 'Pending'}
                                      </Badge>
                                      <Badge variant="outline" className="text-xs">
                                        {quiz.role}
                                      </Badge>
                                    </div>
                                    <div className="text-xs text-muted-foreground space-y-1">
                                      <p>Due: {format(new Date(quiz.due_at), 'MMM d, yyyy h:mm a')}</p>
                                      {quiz.started_at && (
                                        <p>Started: {format(new Date(quiz.started_at), 'MMM d, yyyy h:mm a')}</p>
                                      )}
                                      {quiz.completed_at && (
                                        <p>Completed: {format(new Date(quiz.completed_at), 'MMM d, yyyy h:mm a')}</p>
                                      )}
                                    </div>
                                    {quiz.status === 'completed' && (
                                      <div className="mt-2 p-2 bg-muted/50 rounded">
                                        <p className="text-sm font-medium">
                                          Score: {quiz.score} / {quiz.total_questions}
                                          <span className="text-muted-foreground ml-2">
                                            ({Math.round((quiz.score / quiz.total_questions) * 100)}%)
                                          </span>
                                        </p>
                                      </div>
                                    )}
                                    {quiz.admin_notification_sent && (
                                      <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                        <AlertTriangle className="h-3 w-3 inline mr-1" />
                                        Admin notified on {format(new Date(quiz.admin_notified_at), 'MMM d, yyyy h:mm a')}
                                      </div>
                                    )}
                                  </div>
                                  <div className="text-right text-xs text-muted-foreground whitespace-nowrap ml-4">
                                    {format(new Date(quiz.assigned_at), 'MMM d, yyyy')}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Booking Notes */}
                      <div className="pt-3 border-t mt-3">
                        <div className="flex items-center justify-between mb-3">
                          <h5 className="font-semibold flex items-center gap-2">
                            <MessageSquarePlus className="h-4 w-4" />
                            Booking Notes
                          </h5>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openNoteDialog(assignment.id)}
                          >
                            <MessageSquarePlus className="h-4 w-4 mr-2" />
                            Add Note
                          </Button>
                        </div>
                        {assignment.notes.length === 0 ? (
                          <p className="text-sm text-muted-foreground">No notes yet</p>
                        ) : (
                          <div className="space-y-2">
                            {assignment.notes.map((note: any) => (
                              <div key={note.id} className="bg-muted/50 rounded p-2">
                                <div className="flex justify-between text-xs mb-1">
                                  <span className="font-medium">{note.author}</span>
                                  <span className="text-muted-foreground">
                                    {format(note.date, 'MMM d, yyyy')}
                                  </span>
                                </div>
                                <p className="text-sm">{note.text}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Client-Talent Chat History */}
                      <div className="pt-3 border-t mt-3">
                        <h5 className="font-semibold mb-3 flex items-center gap-2">
                          <MessageSquare className="h-4 w-4" />
                          Client-Talent Chat History
                        </h5>
                        <BookingChatHistory bookingId={assignment.id} />
                      </div>

                      {/* Logistics Questionnaire */}
                      <div className="pt-3 border-t mt-3">
                        <h5 className="font-semibold mb-3 flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          Logistics Questionnaire
                        </h5>
                        {logisticsData[assignment.id] ? (
                          <div className="space-y-3 text-sm">
                            <Badge variant={logisticsData[assignment.id].status === 'submitted' ? 'default' : 'secondary'}>
                              {logisticsData[assignment.id].status || 'Not Started'}
                            </Badge>
                            {logisticsData[assignment.id].answers && (
                              <div className="space-y-2 bg-muted/30 rounded-lg p-4">
                                {(() => {
                                  const answers = logisticsData[assignment.id].answers;
                                  const fields = [
                                    { key: 'couple_names', label: 'Couple Names' },
                                    { key: 'wedding_date', label: 'Wedding Date' },
                                    { key: 'venue_name', label: 'Venue' },
                                    { key: 'ceremony_time', label: 'Ceremony Time' },
                                    { key: 'reception_time', label: 'Reception Time' },
                                    { key: 'guest_count', label: 'Guest Count' },
                                    { key: 'first_look', label: 'First Look' },
                                    { key: 'first_look_time', label: 'First Look Time' },
                                    { key: 'first_look_location', label: 'First Look Location' },
                                    { key: 'bridal_party_size', label: 'Bridal Party Size' },
                                    { key: 'must_have_photos', label: 'Must-Have Photos' },
                                    { key: 'venue_restrictions', label: 'Venue Restrictions' },
                                    { key: 'special_requests', label: 'Special Requests' },
                                    { key: 'day_of_contact_name', label: 'Day-of Contact' },
                                    { key: 'day_of_contact_phone', label: 'Contact Phone' },
                                    { key: 'parking_info', label: 'Parking Info' },
                                  ];
                                  return fields.map(({ key, label }) => {
                                    const value = answers[key];
                                    if (!value || (typeof value === 'string' && !value.trim())) return null;
                                    return (
                                      <div key={key} className="flex flex-col">
                                        <span className="text-muted-foreground text-xs font-medium">{label}</span>
                                        <span className="text-foreground">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
                                      </div>
                                    );
                                  }).filter(Boolean);
                                })()}
                              </div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-muted-foreground">No questionnaire submitted yet</p>
                        )}
                      </div>

                      {/* Unassign Actions - Show button for each role */}
                      <div className="pt-3 border-t mt-3 space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {assignment.isPhotographer && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassign(assignment.id, 'photographer')}
                              className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                            >
                              <UserMinus className="h-4 w-4 mr-2" />
                              Unassign Photographer
                            </Button>
                          )}
                          {assignment.isVideographer && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassign(assignment.id, 'videographer')}
                              className="border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400"
                            >
                              <UserMinus className="h-4 w-4 mr-2" />
                              Unassign Videographer
                            </Button>
                          )}
                          {assignment.isSecondPhotographer && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleUnassign(assignment.id, 'second_photographer')}
                              className="border-orange-300 text-orange-600 hover:bg-orange-50 hover:border-orange-400"
                            >
                              <UserMinus className="h-4 w-4 mr-2" />
                              Unassign 2nd Photographer
                            </Button>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Remove {talent.name} from this booking to allow reassignment.
                        </p>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </CardContent>


        {/* Past Bookings */}
        <Card>
          <CardHeader>
            <CardTitle>Past Bookings</CardTitle>
            <CardDescription>
              Completed assignments and payment history
            </CardDescription>
          </CardHeader>
          <CardContent>
            {completedAssignments.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                No past bookings
              </p>
            ) : (
              <div className="space-y-4">
                {completedAssignments.map((assignment) => (
                  <Collapsible
                    key={assignment.id}
                    open={expandedBooking === assignment.id}
                    onOpenChange={(open) => setExpandedBooking(open ? assignment.id : null)}
                  >
                    <div className="border rounded-lg p-4 bg-muted/20 space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold">
                            {assignment.customerId ? (
                              <span
                                className="text-primary hover:underline cursor-pointer"
                                onClick={() => navigate(consoleLink(`/lp-console/clients/${assignment.customerId}`))}
                              >
                                {assignment.clientName}
                              </span>
                            ) : (
                              assignment.clientName
                            )}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {assignment.eventCity}, {assignment.eventState} • {format(assignment.eventDate, 'MMMM d, yyyy')}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          {/* Role badges */}
                          {assignment.isPhotographer && (
                            <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                              📷 Photographer
                            </Badge>
                          )}
                          {assignment.isVideographer && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              🎥 Videographer
                            </Badge>
                          )}
                          {assignment.isSecondPhotographer && (
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                              📷² 2nd Photographer
                            </Badge>
                          )}
                          <Badge variant="outline" className={getStatusColor(assignment.status)}>
                            {assignment.status}
                          </Badge>
                        </div>
                      </div>

                      {/* Photographer Readiness Indicators - Always Visible */}
                      <div className="pt-2 border-t">
                        <p className="text-xs text-muted-foreground mb-2">Photographer Confirmations:</p>
                        <div className="flex flex-wrap gap-2">
                          {/* Client Outreach Status */}
                          {assignment.client_outreach_confirmed ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Client Outreach Complete
                              {assignment.client_outreach_confirmed_at && (
                                <span className="ml-1 text-xs">
                                  ({format(new Date(assignment.client_outreach_confirmed_at), 'MMM d')})
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Client Outreach Not Confirmed
                            </Badge>
                          )}

                          {/* Event Ready Status */}
                          {assignment.event_ready_confirmed ? (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Event Ready
                              {assignment.event_ready_confirmed_at && (
                                <span className="ml-1 text-xs">
                                  ({format(new Date(assignment.event_ready_confirmed_at), 'MMM d')})
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-gray-100 text-gray-500 border-gray-200">
                              <XCircle className="h-3 w-3 mr-1" />
                              Event Ready Not Confirmed
                            </Badge>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-2 border-t">
                        <div>
                          <p className="text-xs text-muted-foreground">
                            {assignment.talent_paid ? 'Amount Paid' : 'Talent Rate'}
                            {!assignment.talent_paid && assignment.hoursBooked > 0 && (
                              <span className="ml-1">({assignment.hoursBooked}h × $40)</span>
                            )}
                          </p>
                          <p className={`font-semibold ${assignment.talent_paid ? 'text-green-600' : ''}`}>
                            ${assignment.talent_paid && assignment.actualPaidAmount != null
                              ? assignment.actualPaidAmount.toLocaleString()
                              : assignment.rate.toLocaleString()}
                            {!assignment.talent_paid && <span className="text-xs text-muted-foreground ml-1">(unpaid)</span>}
                          </p>
                        </div>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="sm">
                            {expandedBooking === assignment.id ? 'Hide Details' : 'Show Details'}
                          </Button>
                        </CollapsibleTrigger>
                      </div>

                      {/* Collapsible Details */}
                      <CollapsibleContent className="space-y-4 pt-4 border-t">
                        {/* Same sections as Active Bookings */}
                        {/* Email Notifications for This Booking */}
                        <div>
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <Mail className="h-4 w-4" />
                            Email Notifications ({assignment.email_schedules?.length || 0})
                          </h5>
                          {!assignment.email_schedules || assignment.email_schedules.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No emails sent for this booking</p>
                          ) : (
                            <div className="space-y-2">
                              {assignment.email_schedules
                                .sort((a: any, b: any) => new Date(b.scheduled_for).getTime() - new Date(a.scheduled_for).getTime())
                                .map((email: any) => (
                                <Collapsible key={email.id}>
                                  <div className="border rounded-lg p-3 bg-muted/30">
                                    <div className="flex items-center justify-between gap-3">
                                      <div className="flex items-center gap-2 flex-1 min-w-0">
                                        <Badge
                                          variant={
                                            email.status === 'sent' ? 'default' :
                                            email.status === 'failed' ? 'destructive' :
                                            email.status === 'cancelled' ? 'secondary' :
                                            'outline'
                                          }
                                          className={`shrink-0 ${email.status === 'failed' ? 'text-white' : ''}`}
                                        >
                                          {email.status}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs shrink-0">
                                          {email.email_type}
                                        </Badge>
                                        <span className="text-sm truncate">{email.recipient_email}</span>
                                        {email.error_message && (
                                          <AlertCircleIcon className="h-4 w-4 text-destructive shrink-0" />
                                        )}
                                      </div>
                                      <div className="flex items-center gap-2 shrink-0">
                                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                                          {format(new Date(email.scheduled_for), 'MMM d, h:mm a')}
                                        </span>
                                        <CollapsibleTrigger asChild>
                                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                            <ChevronDown className="h-4 w-4" />
                                          </Button>
                                        </CollapsibleTrigger>
                                      </div>
                                    </div>

                                    <CollapsibleContent className="pt-3 border-t mt-3">
                                      <div className="space-y-2 text-xs text-muted-foreground">
                                        {email.recipient_name && (
                                          <p><span className="font-medium">Name:</span> {email.recipient_name}</p>
                                        )}
                                        <p><span className="font-medium">Recipient Type:</span> {email.recipient_type}</p>
                                        <p><span className="font-medium">Scheduled For:</span> {format(new Date(email.scheduled_for), 'MMM d, yyyy h:mm a')}</p>
                                        {email.sent_at && (
                                          <p><span className="font-medium">Sent At:</span> {format(new Date(email.sent_at), 'MMM d, yyyy h:mm a')}</p>
                                        )}
                                        {email.resend_email_id && (
                                          <p className="font-mono"><span className="font-medium font-sans">Email ID:</span> {email.resend_email_id}</p>
                                        )}
                                        <p><span className="font-medium">Created:</span> {format(new Date(email.created_at), 'MMM d, yyyy h:mm a')}</p>
                                      </div>
                                      {email.error_message && (
                                        <div className="mt-3 p-2 bg-destructive/10 rounded text-xs text-destructive">
                                          <span className="font-medium">Error:</span> {email.error_message}
                                        </div>
                                      )}
                                    </CollapsibleContent>
                                  </div>
                                </Collapsible>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Photo Upload Reminders for This Booking */}
                        <div className="pt-3 border-t mt-3">
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <Bell className="h-4 w-4" />
                            Photo Upload Reminders ({assignment.photographer_reminder_attempts?.length || 0})
                          </h5>
                          {!assignment.photographer_reminder_attempts || assignment.photographer_reminder_attempts.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No photo upload reminders sent for this booking</p>
                          ) : (
                            <div className="space-y-2">
                              {assignment.photographer_reminder_attempts
                                .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                                .map((attempt: any) => (
                                <div key={attempt.id} className="border rounded-lg p-3 bg-muted/30">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Badge
                                          variant={
                                            attempt.delivery_status === 'delivered' ? 'default' :
                                            attempt.delivery_status === 'failed' ? 'destructive' :
                                            'secondary'
                                          }
                                          className={`shrink-0 ${attempt.delivery_status === 'failed' ? 'text-white' : ''}`}
                                        >
                                          {attempt.delivery_status || 'pending'}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">
                                          Reminder #{attempt.reminder_count}
                                        </span>
                                      </div>
                                      <p className="text-sm font-medium mb-1">
                                        {attempt.email_subject}
                                      </p>
                                      <div className="text-xs text-muted-foreground space-y-1">
                                        <p>To: {attempt.email_sent_to} ({attempt.recipient_role})</p>
                                        {attempt.resend_email_id && (
                                          <p className="font-mono">Email ID: {attempt.resend_email_id}</p>
                                        )}
                                      </div>
                                      {attempt.error_message && (
                                        <div className="mt-2 p-2 bg-destructive/10 rounded text-xs text-destructive">
                                          Error: {attempt.error_message}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground whitespace-nowrap ml-4">
                                      {format(new Date(attempt.created_at), 'MMM d, h:mm a')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Pre-Event Quizzes for This Booking */}
                        <div className="pt-3 border-t mt-3">
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <CheckCircle className="h-4 w-4" />
                            Pre-Event Quizzes ({assignment.talent_quizzes?.length || 0})
                          </h5>
                          {!assignment.talent_quizzes || assignment.talent_quizzes.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No quizzes assigned for this booking</p>
                          ) : (
                            <div className="space-y-2">
                              {assignment.talent_quizzes.map((quiz: any) => (
                                <div key={quiz.id} className="border rounded-lg p-3 bg-muted/30">
                                  <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <Badge
                                          className="text-white"
                                          variant={
                                            quiz.status === 'completed' && quiz.passed ? 'default' :
                                            quiz.status === 'completed' && !quiz.passed ? 'destructive' :
                                            quiz.status === 'in_progress' ? 'secondary' :
                                            new Date(quiz.due_at) < new Date() ? 'destructive' :
                                            'outline'
                                          }
                                        >
                                          {quiz.status === 'completed' && quiz.passed && (
                                            <CheckCircle className="h-3 w-3 mr-1" />
                                          )}
                                          {quiz.status === 'completed' && !quiz.passed && (
                                            <XCircle className="h-3 w-3 mr-1" />
                                          )}
                                          {quiz.status === 'in_progress' && (
                                            <Clock className="h-3 w-3 mr-1" />
                                          )}
                                          {quiz.status === 'pending' && new Date(quiz.due_at) < new Date() && (
                                            <AlertTriangle className="h-3 w-3 mr-1" />
                                          )}
                                          {quiz.status === 'completed'
                                            ? (quiz.passed ? 'Passed' : 'Failed')
                                            : quiz.status === 'in_progress'
                                            ? 'In Progress'
                                            : new Date(quiz.due_at) < new Date()
                                            ? 'Overdue'
                                            : 'Pending'}
                                        </Badge>
                                        <Badge variant="outline" className="text-xs">
                                          {quiz.role}
                                        </Badge>
                                      </div>
                                      <div className="text-xs text-muted-foreground space-y-1">
                                        <p>Due: {format(new Date(quiz.due_at), 'MMM d, yyyy h:mm a')}</p>
                                        {quiz.started_at && (
                                          <p>Started: {format(new Date(quiz.started_at), 'MMM d, yyyy h:mm a')}</p>
                                        )}
                                        {quiz.completed_at && (
                                          <p>Completed: {format(new Date(quiz.completed_at), 'MMM d, yyyy h:mm a')}</p>
                                        )}
                                      </div>
                                      {quiz.status === 'completed' && (
                                        <div className="mt-2 p-2 bg-muted/50 rounded">
                                          <p className="text-sm font-medium">
                                            Score: {quiz.score} / {quiz.total_questions}
                                            <span className="text-muted-foreground ml-2">
                                              ({Math.round((quiz.score / quiz.total_questions) * 100)}%)
                                            </span>
                                          </p>
                                        </div>
                                      )}
                                      {quiz.admin_notification_sent && (
                                        <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                                          <AlertTriangle className="h-3 w-3 inline mr-1" />
                                          Admin notified on {format(new Date(quiz.admin_notified_at), 'MMM d, yyyy h:mm a')}
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground whitespace-nowrap ml-4">
                                      {format(new Date(quiz.assigned_at), 'MMM d, yyyy')}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Booking Notes */}
                        <div className="pt-3 border-t mt-3">
                          <div className="flex items-center justify-between mb-3">
                            <h5 className="font-semibold flex items-center gap-2">
                              <MessageSquarePlus className="h-4 w-4" />
                              Booking Notes
                            </h5>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border border-gray-300 text-gray-800 hover:bg-gray-100"
                              onClick={() => openNoteDialog(assignment.id)}
                            >
                              <MessageSquarePlus className="h-4 w-4 mr-2" />
                              Add Note
                            </Button>
                          </div>
                          {assignment.notes.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No notes yet</p>
                          ) : (
                            <div className="space-y-2">
                              {assignment.notes.map((note: any) => (
                                <div key={note.id} className="bg-muted/50 rounded p-2">
                                  <div className="flex justify-between text-xs mb-1">
                                    <span className="font-medium">{note.author}</span>
                                    <span className="text-muted-foreground">
                                      {format(note.date, 'MMM d, yyyy')}
                                    </span>
                                  </div>
                                  <p className="text-sm">{note.text}</p>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Client-Talent Chat History */}
                        <div className="pt-3 border-t mt-3">
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <MessageSquare className="h-4 w-4" />
                            Client-Talent Chat History
                          </h5>
                          <BookingChatHistory bookingId={assignment.id} />
                        </div>

                        {/* Logistics Questionnaire */}
                        <div className="pt-3 border-t mt-3">
                          <h5 className="font-semibold mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Logistics Questionnaire
                          </h5>
                          {logisticsData[assignment.id] ? (
                            <div className="space-y-3 text-sm">
                              <Badge variant={logisticsData[assignment.id].status === 'submitted' ? 'default' : 'secondary'}>
                                {logisticsData[assignment.id].status || 'Not Started'}
                              </Badge>
                              {logisticsData[assignment.id].answers && (
                                <div className="space-y-2 bg-muted/30 rounded-lg p-4">
                                  {(() => {
                                    const answers = logisticsData[assignment.id].answers;
                                    const fields = [
                                      { key: 'couple_names', label: 'Couple Names' },
                                      { key: 'wedding_date', label: 'Wedding Date' },
                                      { key: 'venue_name', label: 'Venue' },
                                      { key: 'ceremony_time', label: 'Ceremony Time' },
                                      { key: 'reception_time', label: 'Reception Time' },
                                      { key: 'guest_count', label: 'Guest Count' },
                                      { key: 'first_look', label: 'First Look' },
                                      { key: 'first_look_time', label: 'First Look Time' },
                                      { key: 'first_look_location', label: 'First Look Location' },
                                      { key: 'bridal_party_size', label: 'Bridal Party Size' },
                                      { key: 'must_have_photos', label: 'Must-Have Photos' },
                                      { key: 'venue_restrictions', label: 'Venue Restrictions' },
                                      { key: 'special_requests', label: 'Special Requests' },
                                      { key: 'day_of_contact_name', label: 'Day-of Contact' },
                                      { key: 'day_of_contact_phone', label: 'Contact Phone' },
                                      { key: 'parking_info', label: 'Parking Info' },
                                    ];
                                    return fields.map(({ key, label }) => {
                                      const value = answers[key];
                                      if (!value || (typeof value === 'string' && !value.trim())) return null;
                                      return (
                                        <div key={key} className="flex flex-col">
                                          <span className="text-muted-foreground text-xs font-medium">{label}</span>
                                          <span className="text-foreground">{typeof value === 'boolean' ? (value ? 'Yes' : 'No') : value}</span>
                                        </div>
                                      );
                                    }).filter(Boolean);
                                  })()}
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground">No questionnaire submitted yet</p>
                          )}
                        </div>
                      </CollapsibleContent>
                    </div>
                  </Collapsible>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Unassign Confirmation Dialog */}
      <AlertDialog open={unassignConfirmOpen} onOpenChange={setUnassignConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Removal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove {talent?.name} as{' '}
              {roleToUnassign === 'photographer' ? 'Photographer' :
               roleToUnassign === 'videographer' ? 'Videographer' :
               roleToUnassign === 'second_photographer' ? '2nd Photographer' : 'talent'}{' '}
              from this booking?
              {roleToUnassign === 'second_photographer'
                ? ' The 2nd photographer slot will be emptied.'
                : ' The booking will be reassigned to the default L+P placeholder.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setAssignmentToUnassign(null); setRoleToUnassign(null); }}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmUnassign}>Remove Assignment</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Talent Records & History: the new console only (prototype v108), Admin and Talent */}
      {staging && talent?.id && (viewRole === 'admin' || viewRole === 'talent_coordinator') && <TalentRecordsCard talentId={talent.id} name={talent.name} />}

      {/* General Talent Notes */}
      <Card>
        <CardHeader>
          <CardTitle>General Talent Notes</CardTitle>
          <CardDescription>Internal notes about this talent member</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Textarea
                placeholder="Add a note about this talent..."
                value={newTalentNote}
                onChange={(e) => setNewTalentNote(e.target.value)}
                className="flex-1"
              />
              <Button 
                variant="outline"
                size="sm"
                className="border border-gray-300 text-gray-800 hover:bg-gray-100"
                onClick={handleAddTalentNote} disabled={!newTalentNote.trim()}
              >
                Add Note
              </Button>
            </div>
            
            <div className="space-y-3">
              {talentNotes.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No notes yet</p>
              ) : (
                talentNotes.map((note) => (
                  <div key={note.id} className="border rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <p className="text-sm font-medium">{note.created_by_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(note.created_at), 'MMM d, yyyy h:mm a')}
                      </p>
                    </div>
                    <p className="text-sm">{note.note}</p>
                  </div>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note to Booking</DialogTitle>
            <DialogDescription>
              Add a note for this talent assignment
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div>
              <Label htmlFor="note">Note</Label>
              <Textarea
                id="note"
                placeholder="Enter note..."
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="mt-2 min-h-[100px]"
              />
            </div>
            <Button 
              onClick={handleAddNote} 
              disabled={!newNote.trim()}
              className="w-full"
            >
              Add Note
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin Chat Panel */}
      {isAdminChatOpen && talent && talent.user_id && (
        <AdminChatPanel
          userId={talent.user_id}
          userName={talent.name}
          userRole="photographer"
          onClose={() => setIsAdminChatOpen(false)}
        />
      )}
    </div>
  );
};

export default TalentDetail;
